<script setup lang="ts">
  import { Bot, MessageSquare } from 'lucide-vue-next'
  import { useTranslation } from '@/composables'
  import { useAiAssistantBeta } from '@/components/agent-actions/ai-assistant-beta/useAiAssistantBeta'

  export type SidePanelTab = 'chat' | 'ai_assistant'

  // Defense-in-depth: even if a future caller mounts the tab bar without checking the
  // feature flag at the parent, the component renders nothing when the flag is off.
  const { shouldShowSidePanelTab } = useAiAssistantBeta()

  const active = defineModel<SidePanelTab>('active', { required: true })

  // Only the AI suggestions label translates ("AI suggestions" / "KI-Vorschläge").
  // Chat and Beta are identical across locales, so they're inlined as literals.
  const { t } = useTranslation({
    en: { aiSuggestions: 'AI suggestions' },
    de: { aiSuggestions: 'KI-Vorschläge' }
  })

  function select(tab: SidePanelTab) {
    active.value = tab
  }
</script>

<template>
  <div
    v-if="shouldShowSidePanelTab"
    class="px-4 pb-4 pt-2"
  >
    <div
      role="tablist"
      class="flex h-14 w-full items-center justify-center gap-0"
    >
      <button
        type="button"
        role="tab"
        class="flex h-full flex-1 flex-col items-center justify-center gap-1 rounded-lg transition-colors"
        :class="active === 'chat' ? 'bg-[#F9FAFB]' : 'hover:bg-[#F9FAFB]/60'"
        :aria-selected="active === 'chat'"
        @click="select('chat')"
      >
        <MessageSquare
          :stroke-width="1.5"
          class="h-6 w-6"
          :class="active === 'chat' ? 'text-[#9333EA]' : 'text-[#4B5563]'"
        />
        <span
          class="text-xs font-normal leading-4"
          :class="active === 'chat' ? 'text-[#9333EA]' : 'text-[#4B5563]'"
        >
          Chat
        </span>
      </button>

      <button
        type="button"
        role="tab"
        class="flex h-full flex-1 flex-col items-center justify-center gap-1 rounded-lg transition-colors"
        :class="active === 'ai_assistant' ? 'bg-[#F9FAFB]' : 'hover:bg-[#F9FAFB]/60'"
        :aria-selected="active === 'ai_assistant'"
        @click="select('ai_assistant')"
      >
        <div class="relative">
          <Bot
            :stroke-width="1.5"
            class="h-6 w-6"
            :class="active === 'ai_assistant' ? 'text-[#9333EA]' : 'text-[#4B5563]'"
          />
          <span
            class="absolute -right-3 -top-1 text-[10px] font-semibold leading-none text-[#D8B4FE]"
          >
            Beta
          </span>
        </div>
        <span
          v-if="active === 'ai_assistant'"
          class="bg-gradient-to-l from-[#9333EA] to-[#A855F7] bg-clip-text text-xs font-medium leading-4
            text-transparent"
        >
          {{ t('aiSuggestions') }}
        </span>
        <span
          v-else
          class="text-xs font-medium leading-4 text-[#4B5563]"
        >
          {{ t('aiSuggestions') }}
        </span>
      </button>
    </div>
  </div>
</template>
